import React, { useEffect, useState, useContext, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { defaultMessages } from '../../../libs/i18n/default'
import { AsYouType } from 'libphonenumber-js/max'
import FormControl from 'react-bootstrap/lib/FormControl'
import Button from 'react-bootstrap/lib/Button'
import PhoneContext from './PhoneContext'
import PhoneErrors from './PhoneErrors'

const OtpInput = ({ code, setCode, disabled }) => {
  const inputs = useRef([]);

  const handleChange = (e, index) => {
    const val = e.target.value;
    if (/[^0-9]/.test(val)) return;

    const newCode = code.split('');
    newCode[index] = val;
    const newCodeStr = newCode.join('');
    setCode(newCodeStr.padEnd(code.length, ' ').slice(0, 6).trim());

    if (val && index < 5) {
      inputs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace' && !code[index] && index > 0) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, 6);
    if (paste) {
      setCode(paste);
      const nextIndex = Math.min(paste.length, 5);
      inputs.current[nextIndex]?.focus();
    }
  };

  return (
    <div className="otp-input-wrapper" style={{ display: 'flex', justifyContent: 'center', marginBottom: '20px' }}>
      {[0, 1, 2, 3, 4, 5].map((index) => (
        <input
        id={`code_${index}`}
          key={index}
          ref={(el) => (inputs.current[index] = el)}
          type="text"
          maxLength="1"
          className="form-control otp-digit"
          value={code[index] || ''}
          onChange={(e) => handleChange(e, index)}
          onKeyDown={(e) => handleKeyDown(e, index)}
          onPaste={handlePaste}
          disabled={disabled}
        />
      ))}
    </div>
  );
};

const PHONE_PLACEHOLDERS_BY_COUNTRY = {
  AU: '0400 000 000',
  NZ: '021 123 4567',
  US: '(201) 555-0123',
  CA: '(204) 234-5678',
  GB: '07123 456789'
}

const DIAL_CODES_BY_COUNTRY = {
  AU: '61',
  NZ: '64',
  US: '1',
  CA: '1',
  GB: '44'
}

const territoryPhonePlaceholder = countryCode => {
  const country = (countryCode || 'AU').toUpperCase()
  return PHONE_PLACEHOLDERS_BY_COUNTRY[country] || 'Mobile phone number'
}

const PhoneInputField = ({ validations, onVerify, onChange, onUpdate, placeholder, Wrapper, countryCode, disableVerifications, defaultNumber, showInlineErrors = true }) => {
  const [verifyCodeSent, setVerifyCodeSent] = useState(false)
  const [verificationCode, setVerificationCode] = useState('')
  const [verificationResponse, setVerificationResponse] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isVerifying, setIsVerifying] = useState(false)
  const [isEditing, setIsEditing] = useState(!defaultNumber)
  const [displayPhoneNumber, setDisplayPhoneNumber] = useState(defaultNumber || '')
  const [phonePlaceholder, setPhonePlaceholder] = useState(placeholder || territoryPhonePlaceholder(countryCode))
  const [phoneFormatValid, setPhoneFormatValid] = useState(null)
  const [isCheckingPhoneUnique, setIsCheckingPhoneUnique] = useState(false)

  const phoneInputRef = useRef(null)
  const itiRef = useRef(null)

  const {
    phoneUnique,
    phoneNumber,
    errorMessage,
    setPhoneUnique,
    setPhoneNumber,
    setErrorMessage,
    verificationStatus,
    setVerificationStatus
  } = useContext(PhoneContext)

  const selectedCountry = () => itiRef.current?.getSelectedCountryData?.() || {}

  const currentPhonePlaceholder = () => {
    if (placeholder) return placeholder

    const country = selectedCountry().iso2 || countryCode
    const utils = window.intlTelInputUtils
    if (country && utils?.getExampleNumber) {
      const example = utils.getExampleNumber(country?.toLowerCase(), true, utils.numberType?.MOBILE)
      if (example) return example
    }

    return territoryPhonePlaceholder(country)
  }

  const numberForFormatting = value => {
    const dialCode = selectedCountry().dialCode
    const digits = (value || '').replace(/\D/g, '')
    if (!digits) return value

    if (dialCode && value.trim().startsWith('+') && digits.startsWith(dialCode)) {
      const localDigits = digits.slice(dialCode.length).replace(/^0/, '')
      return `+${dialCode}${localDigits}`
    }

    if (dialCode && digits.startsWith('0')) {
      return `+${dialCode}${digits.replace(/^0/, '')}`
    }

    return value
  }

  const localDisplayNumber = value => {
    const country = (countryCode || 'AU').toUpperCase()
    const dialCode = selectedCountry().dialCode || DIAL_CODES_BY_COUNTRY[country]
    let digits = (value || '').replace(/\D/g, '')

    if (dialCode && digits.startsWith(dialCode)) {
      digits = digits.slice(dialCode.length)
    }

    if (country === 'AU' && digits.startsWith('4') && digits.length === 9) {
      digits = `0${digits}`
    }

    if (country === 'AU' && digits.startsWith('04') && digits.length >= 10) {
      return `${digits.slice(0, 4)} ${digits.slice(4, 7)} ${digits.slice(7)}`
    }

    if (country === 'NZ' && digits.startsWith('2') && digits.length >= 8) {
      const localDigits = `0${digits}`
      if (localDigits.length === 10) {
        return `${localDigits.slice(0, 3)} ${localDigits.slice(3, 6)} ${localDigits.slice(6)}`
      }

      const asYouType = new AsYouType('NZ')
      return asYouType.input(localDigits)
    }

    if (dialCode && digits) {
      const asYouType = new AsYouType(country)
      return asYouType.input(digits)
    }

    return (value || '').replace(/^\+\d+\s*/, '').replace(/^\+/, '')
  }

  const formatDisplayNumber = value => {
    if (!value) return ''

    const normalizedNumber = numberForFormatting(value)
    if (itiRef.current?.setNumber && normalizedNumber) {
      itiRef.current.setNumber(normalizedNumber)
    }

    const utils = window.intlTelInputUtils
    const country = selectedCountry().iso2?.toUpperCase()
    if (utils?.formatNumber && utils?.numberFormat?.NATIONAL && normalizedNumber && country) {
      const formatted = utils.formatNumber(normalizedNumber, country, utils.numberFormat.NATIONAL)
      if (formatted) return formatted
    }

    return localDisplayNumber(value)
  }

  const formatDisplayNumberAsYouType = value => {
    if (!value) return ''

    const asYouType = new AsYouType(countryCode ? countryCode.toUpperCase() : 'AU')
    return asYouType.input(value)
  }

  const validPhoneFormat = value => {
    if (!value) return null

    const asYouType = new AsYouType(countryCode ? countryCode.toUpperCase() : 'AU')
    asYouType.input(value)
    return asYouType.isValid()
  }

  const normalizedPhoneNumber = value => {
    const hasExplicitValue = typeof value !== 'undefined'
    const inputValue = hasExplicitValue ? value : (displayPhoneNumber || phoneNumber)

    if (!inputValue) return ''

    const formattedInput = numberForFormatting(inputValue) || inputValue
    if (hasExplicitValue && itiRef.current?.setNumber) {
      itiRef.current.setNumber(formattedInput)
    }

    if (itiRef.current?.getNumber) {
      const number = itiRef.current.getNumber()
      if (number) return number
    }

    const asYouType = new AsYouType(countryCode ? countryCode.toUpperCase() : 'AU')
    asYouType.input(inputValue)
    const parsedNumber = asYouType.getNumberValue()
    if (parsedNumber) return parsedNumber

    return formattedInput
  }

  const clearPhoneNumber = id => {
    if (itiRef.current?.setNumber) {
      itiRef.current.setNumber('')
    }

    if (phoneInputRef.current) {
      phoneInputRef.current.value = ''
    }

    setDisplayPhoneNumber('')
    setPhoneNumber('')
    setPhoneFormatValid(null)
    setPhoneUnique(null)
    setIsCheckingPhoneUnique(false)
    onChange && id && onChange({ target: { value: '', id } })
  }

  useEffect(() => {
    if (phoneInputRef.current && window.intlTelInput) {
      itiRef.current = window.intlTelInput(phoneInputRef.current, {
        initialCountry: countryCode ? countryCode.toLowerCase() : "au",
        utilsScript: "https://cdn.jsdelivr.net/npm/intl-tel-input@23.0.4/build/js/utils.js",
        separateDialCode: true,
        nationalMode: true,
        formatOnDisplay: true,
        autoPlaceholder: "off",
      });

      const syncCountryChange = () => {
        const formatted = formatDisplayNumber(phoneInputRef.current.value)
        setPhonePlaceholder(currentPhonePlaceholder())
        setDisplayPhoneNumber(formatted)
        const normalized = normalizedPhoneNumber(formatted)
        setPhoneNumber(normalized)
        onChange && onChange({ target: { value: normalized, id: phoneInputRef.current.id || 'phone' } })
      };

      const inputElement = phoneInputRef.current;

      inputElement.addEventListener('countrychange', syncCountryChange);

      const initialValue = defaultNumber || inputElement.value
      if (initialValue) {
        const formatted = formatDisplayNumber(initialValue)
        setDisplayPhoneNumber(formatted)
        setPhoneNumber(normalizedPhoneNumber(formatted))
      }

      setPhonePlaceholder(currentPhonePlaceholder())

      return () => {
        inputElement.removeEventListener('countrychange', syncCountryChange);
        if (itiRef.current) {
          itiRef.current.destroy();
        }
      };
    }
  }, [countryCode]);

  useEffect(() => {
    if (placeholder || !itiRef.current) {
      setPhonePlaceholder(placeholder || territoryPhonePlaceholder(countryCode))
    }
  }, [placeholder, countryCode])

  useEffect(() => {
    if (!defaultNumber || !itiRef.current) return

    const formatted = formatDisplayNumber(defaultNumber)
    const normalized = normalizedPhoneNumber(formatted)

    console.warn(`[host-signup] PhoneInputField defaultNumber applied: ${JSON.stringify({
      defaultNumber,
      countryCode,
      formatted,
      normalized
    })}`);

    if (phoneInputRef.current) {
      phoneInputRef.current.value = formatted
    }

    setDisplayPhoneNumber(formatted)
    setPhoneNumber(normalized)
    setPhoneFormatValid(validPhoneFormat(formatted))
    setPhoneUnique(true)
    setIsEditing(false)
  }, [defaultNumber])

  const valid = (phoneFormatValid === false || validations === false)
    ? false
    : (phoneUnique === null ? null : !!phoneUnique && (typeof validations === 'boolean' ? validations : true))
  const phoneReadyToSubmit = !!phoneUnique && valid !== false

  const handleChange = e => {
    const displayNumber = formatDisplayNumberAsYouType(e.target.value)

    if (!displayNumber) {
      clearPhoneNumber(e.target.id)
      setVerifyCodeSent(false)
      setVerificationCode('')
      setVerificationResponse({})
      setErrorMessage(null)
      if (verificationStatus !== 'unverified') {
        setVerificationStatus('unverified')
      }
      return
    }

    const normalized = normalizedPhoneNumber(displayNumber)
    const nextPhoneFormatValid = validPhoneFormat(displayNumber)
    setDisplayPhoneNumber(displayNumber)
    setPhoneNumber(normalized)
    setPhoneFormatValid(nextPhoneFormatValid)
    setPhoneUnique(null)

    setVerifyCodeSent(false)
    setVerificationCode('')
    setVerificationResponse({})
    setErrorMessage(null)
    if (verificationStatus !== 'unverified') {
      setVerificationStatus('unverified')
    }

    onChange && onChange({ target: { value: normalized, id: e.target.id } })
  }

  const handleBlur = () => {
    const currentValue = phoneInputRef.current?.value || displayPhoneNumber

    if (!currentValue) {
      clearPhoneNumber(phoneInputRef.current?.id || 'phone')
      return
    }

    const formatted = formatDisplayNumber(currentValue)
    const normalized = normalizedPhoneNumber(formatted)
    setDisplayPhoneNumber(formatted)
    setPhoneNumber(normalized)
    setPhoneFormatValid(validPhoneFormat(formatted))

    onChange && onChange({ target: { value: normalized, id: phoneInputRef.current?.id || 'phone' } })
  }

  const handleUpdate = async (e) => {
    e.preventDefault();
    const normalized = normalizedPhoneNumber()

    if (!phoneFormatValid) {
      setErrorMessage(<FormattedMessage id="client.phone_verification.errors.invalid_format" defaultMessage="Please enter your phone number in the correct format." />);
      return
    }

    if (isCheckingPhoneUnique || phoneUnique === null) {
      setErrorMessage(<FormattedMessage id="client.phone_verification.errors.check_pending" defaultMessage="Please wait while we check your phone number." />);
      return
    }

    if (!phoneUnique) {
      setErrorMessage(<FormattedMessage id="client.phone_verification.errors.already_registered" defaultMessage="This phone number is already registered in our system. Please sign in." />);
      return
    }

    if (onUpdate) {
      setIsSubmitting(true);
      try {
        await onUpdate(normalized);
        setPhoneUnique(true);
        setIsEditing(false);
        setErrorMessage(null);
      } catch (err) {
        setErrorMessage(err.message || <FormattedMessage id="client.phone_verification.errors.update_failed" defaultMessage="Failed to update phone number." />);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setIsEditing(false);
    }
  }

  const cancelEdit = e => {
    e.preventDefault()

    const formatted = formatDisplayNumber(defaultNumber)
    const normalized = normalizedPhoneNumber(formatted)

    setDisplayPhoneNumber(formatted)
    setPhoneNumber(normalized)
    setPhoneFormatValid(validPhoneFormat(formatted))
    setPhoneUnique(true)
    setErrorMessage(null)
    setVerificationCode('')
    setVerificationResponse({})
    setVerifyCodeSent(false)
    setIsEditing(false)

    onChange && onChange({ target: { value: normalized, id: phoneInputRef.current?.id || 'phone' } })
  }

  const sendVerification = e => {
    e.preventDefault();
    setIsSubmitting(true);

    const params = { number: normalizedPhoneNumber(), service_name: 'Homestay' }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    fetch('/phone_verifications', {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(params)
    })
      .then(async (response) => {
        const data = await response.json()
        if (response.ok) {
          setVerificationResponse(data)
          setVerifyCodeSent(true)
          setErrorMessage(null)
        } else {
          setVerificationResponse({ status: 'failed' })
          setErrorMessage(data.error || <FormattedMessage id="client.phone_verification.errors.send_failed" defaultMessage="Failed to send verification code." />)
        }
        setIsSubmitting(false)
      })
      .catch(() => {
        setVerificationResponse({ status: 'failed' })
        setErrorMessage(<FormattedMessage id="client.phone_verification.errors.send_failed" defaultMessage="Failed to send verification code." />)
        setIsSubmitting(false)
      })
  }

  const verifyPhone = e => {
    e.preventDefault();
    setIsVerifying(true);

    const params = { code: verificationCode }

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    fetch(`/phone_verifications/${verificationResponse.token}`, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrfToken
      },
      body: JSON.stringify(params)
    })
      .then(async (response) => {
        const data = await response.json()
        if (response.ok) {
          setVerificationStatus(data.status)
          setErrorMessage(null)
          onVerify && onVerify(normalizedPhoneNumber(), data.verification_id, verificationCode)
        } else {
          const status = data.status || 'failed'
          const nextStatus = status === 'already_used' ? 'failed' : status
          const message = status === 'already_used'
            ? <FormattedMessage id="client.phone_verification.errors.incorrect_code" defaultMessage="Incorrect verification code. Please try again." />
            : data.error || <FormattedMessage id="client.phone_verification.errors.verify_failed" defaultMessage="Verification failed" />

          setVerificationStatus(nextStatus)
          setErrorMessage(message)

          if (status === 'expired') {
            setVerifyCodeSent(false)
          }
        }
        setIsVerifying(false)
      })
      .catch(() => {
        setVerificationStatus('failed')
        setErrorMessage(<FormattedMessage id="client.phone_verification.errors.verify_failed" defaultMessage="Verification failed" />)
        setIsVerifying(false)
      })
  }

  useEffect(() => {
    let cancelled = false
    setPhoneUnique(null)
    setIsCheckingPhoneUnique(false)

    if (!phoneNumber) {
      setPhoneFormatValid(null)
      return undefined
    }

    const nextPhoneFormatValid = validPhoneFormat(phoneNumber)
    setPhoneFormatValid(nextPhoneFormatValid)
    if (!nextPhoneFormatValid || validations === false) return undefined
    setIsCheckingPhoneUnique(true)

    const checkPhoneUnique = async () => {
      try {
        const response = await fetch(`/phone_numbers/check_unique?phone=${encodeURIComponent(phoneNumber)}`, {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            'X-Requested-With': 'XMLHttpRequest',
            'Accept': 'application/json'
          }
        })
        if (!response.ok) throw new Error('Network response was not ok')
        const data = await response.json()
        if (cancelled) return
        setPhoneUnique(data.is_unique)
        setErrorMessage(null)
      } catch (e) {
        if (cancelled) return
        setPhoneUnique(false)
        setErrorMessage(<FormattedMessage id="client.phone_verification.errors.check_failed" defaultMessage="Failed to check phone number uniqueness." />)
      } finally {
        if (!cancelled) setIsCheckingPhoneUnique(false)
      }
    }

    checkPhoneUnique()
    return () => { cancelled = true }
  }, [phoneNumber, validations, countryCode])

  useEffect(() => {
    if (!defaultNumber) return

    const formatted = formatDisplayNumber(defaultNumber)
    const normalized = normalizedPhoneNumber(formatted)

    console.warn(`[host-signup] PhoneInputField defaultNumber rendered: ${JSON.stringify({
      defaultNumber,
      countryCode,
      formatted,
      normalized
    })}`);

    if (phoneInputRef.current) {
      phoneInputRef.current.value = formatted
    }

    setDisplayPhoneNumber(formatted)
    setPhoneNumber(normalized)
    setPhoneFormatValid(validPhoneFormat(formatted))
    setPhoneUnique(true)
  }, [defaultNumber])

  return (
    <>
      {!verifyCodeSent && (
        Wrapper ? (
          <Wrapper valid={valid} validating={isCheckingPhoneUnique} name='phone'>
            <div className="iti-react-wrapper">
              <FormControl
                id="phone"
                inputRef={(el) => { phoneInputRef.current = el; }}
                placeholder={phonePlaceholder}
                type="text"
                componentClass="input"
                value={displayPhoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting || !isEditing}
              />
            </div>
            {showInlineErrors && <PhoneErrors validations={validations} displayType="alert" />}
          </Wrapper>
        ) : (
          <>
            <div className="iti-react-wrapper">
              <FormControl
                id="phone"
                inputRef={(el) => { phoneInputRef.current = el; }}
                placeholder={phonePlaceholder}
                type="text"
                componentClass="input"
                value={displayPhoneNumber}
                onChange={handleChange}
                onBlur={handleBlur}
                disabled={isSubmitting || !isEditing}
              />
            </div>
            {showInlineErrors && <PhoneErrors validations={validations} displayType="alert" />}
          </>
        )
      )}
      {(!!defaultNumber || phoneUnique) && verificationStatus !== 'verified' && !disableVerifications && (
        <div className="mb-2 phone-verification-container" style={{ width: '100%', justifyContent: 'center' }}>
          {!verifyCodeSent && (
            <>
              {isEditing && !!defaultNumber ? (
                <>
                  <Button variant="success" className="update-verify-phone btn-success" disabled={isSubmitting} onClick={handleUpdate}>
                    <FormattedMessage id="client.phone_verification.buttons.update" defaultMessage="Update" />
                  </Button>
                  <Button variant="link" className="cancel-verify-phone text-muted ml-2" disabled={isSubmitting} onClick={cancelEdit}>
                    <FormattedMessage id="client.phone_verification.buttons.cancel" defaultMessage="Cancel" />
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="success" className="send-verify-phone btn-success" disabled={verifyCodeSent || isSubmitting || !phoneReadyToSubmit} onClick={sendVerification}>
                    {isSubmitting ? <i className="fa fa-spinner fa-spin" /> : <FormattedMessage id="client.phone_verification.buttons.send_sms" defaultMessage="Send Code via SMS" />}
                  </Button>
                  {!isEditing && !isSubmitting && (
                    <Button variant="link" className="change-verify-phone text-muted ml-2" onClick={(e) => { e.preventDefault(); setIsEditing(true); }}>
                      <FormattedMessage id="client.phone_verification.buttons.change_number" defaultMessage="Change number" />
                    </Button>
                  )}
                </>
              )}
            </>
          )}
          {verifyCodeSent && (
            <div className="verify-code-container" style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div className="alert alert-info" style={{ width: '100%', textAlign: 'left', marginBottom: '20px' }}>
                <FormattedMessage 
                  id="client.phone_verification.messages.code_sent" 
                  defaultMessage="A verification code has been sent to ******{last4}. Please enter it below." 
                  values={{ last4: (displayPhoneNumber || '').replace(/\D/g, '').slice(-4) }} 
                />
              </div>
              <label style={{ display: "block", marginBottom: "15px", textAlign: "center" }}>
                <FormattedMessage id="client.phone_verification.labels.enter_code" defaultMessage="Enter the 6-digit verification code" />
              </label>
              <OtpInput
                code={verificationCode}
                setCode={(val) => {
                  setVerificationCode(val);
                  if (verificationStatus === 'failed') setVerificationStatus('unverified');
                  if (errorMessage) setErrorMessage(null);
                }}
                disabled={isVerifying}
              />
              <div style={{ display: 'flex', justifyContent: 'center', marginTop: '10px' }}>
                <Button variant="success" className="verify-phone btn-success" onClick={verifyPhone} disabled={verificationCode.length !== 6 || verificationStatus === 'failed' || isVerifying}>
                  {isVerifying ? <i className="fa fa-spinner fa-spin" /> : <FormattedMessage id="client.phone_verification.buttons.confirm_code" defaultMessage="Confirm Code" />}
                </Button>
                <Button variant="link" className="cancel-verify-phone text-muted ml-2" onClick={(e) => { e.preventDefault(); setVerifyCodeSent(false); }} style={{ marginLeft: '10px' }}>
                  <FormattedMessage id="client.phone_verification.buttons.change_number" defaultMessage="Change number" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  )
}

export default PhoneInputField
